import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, Gift, Users, DollarSign, Copy, Share2,
  Trophy, Zap, CheckCircle2, Star, ArrowRight
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const DailyDropReferrals = () => {
  const navigate = useNavigate();
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const response = await api.get('/daily-drop/referrals');
      setReferralData(response.data);
    } catch (error) {
      console.error('Error fetching referral data:', error);
      // Mock data
      setReferralData({
        referralCode: 'TERRY2026',
        referralLink: `${window.location.origin}/?ref=TERRY2026`,
        stats: {
          totalReferrals: 5,
          activeReferrals: 3,
          pendingRewards: 15.00,
          totalEarned: 45.00
        },
        referrals: [
          { id: '1', name: 'John D.', date: '2026-02-15', status: 'active', reward: 10.00 },
          { id: '2', name: 'Sarah M.', date: '2026-02-10', status: 'active', reward: 10.00 },
          { id: '3', name: 'Mike R.', date: '2026-02-08', status: 'pending', reward: 10.00 },
        ],
        rewards: [
          { tier: 'Bronze', referrals: 5, reward: '$25 credit', achieved: true },
          { tier: 'Silver', referrals: 15, reward: '1 month Pro free', achieved: false },
          { tier: 'Gold', referrals: 50, reward: '3 months Pro free', achieved: false },
          { tier: 'Diamond', referrals: 100, reward: 'Lifetime Pro', achieved: false },
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const shareReferral = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join DailyDrop AI',
          text: 'Create viral content daily with AI! Use my referral link:',
          url: referralData?.referralLink
        });
      } catch (error) {
        copyToClipboard(referralData?.referralLink, 'Referral link');
      }
    } else {
      copyToClipboard(referralData?.referralLink, 'Referral link');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Header */}
      <header className="border-b border-purple-500/20 bg-black/30 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              className="text-purple-200 hover:text-white hover:bg-purple-500/20"
              onClick={() => navigate('/daily-drop')}
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Gift className="h-5 w-5 text-amber-400" />
                Referral Program
              </h1>
              <p className="text-xs text-purple-300">Invite friends & earn rewards</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Hero Section */}
        <Card className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500/30 backdrop-blur-sm overflow-hidden">
          <CardContent className="p-8 text-center relative">
            <div className="absolute top-0 right-0 opacity-10">
              <Gift className="h-48 w-48 text-amber-500" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">Earn While You Share</h2>
            <p className="text-amber-200/80 mb-6 max-w-md mx-auto">
              Invite friends to DailyDrop AI and earn rewards for every successful referral!
            </p>
            <div className="flex justify-center items-center gap-4 flex-wrap">
              <div className="bg-black/30 px-6 py-3 rounded-xl">
                <p className="text-amber-300/70 text-xs">Your Referral Code</p>
                <p className="text-2xl font-bold text-white">{referralData?.referralCode}</p>
              </div>
              <Button
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 h-14"
                onClick={shareReferral}
                data-testid="share-button"
              >
                <Share2 className="h-5 w-5 mr-2" />
                Share & Earn
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
            <CardContent className="p-4 text-center">
              <Users className="h-6 w-6 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{referralData?.stats?.totalReferrals || 0}</p>
              <p className="text-purple-300/60 text-xs">Total Referrals</p>
            </CardContent>
          </Card>
          <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="h-6 w-6 text-green-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{referralData?.stats?.activeReferrals || 0}</p>
              <p className="text-purple-300/60 text-xs">Active Users</p>
            </CardContent>
          </Card>
          <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
            <CardContent className="p-4 text-center">
              <DollarSign className="h-6 w-6 text-amber-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">${referralData?.stats?.pendingRewards?.toFixed(2) || '0.00'}</p>
              <p className="text-purple-300/60 text-xs">Pending Rewards</p>
            </CardContent>
          </Card>
          <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
            <CardContent className="p-4 text-center">
              <Trophy className="h-6 w-6 text-purple-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">${referralData?.stats?.totalEarned?.toFixed(2) || '0.00'}</p>
              <p className="text-purple-300/60 text-xs">Total Earned</p>
            </CardContent>
          </Card>
        </div>

        {/* Referral Link */}
        <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white">Your Referral Link</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                value={referralData?.referralLink || ''}
                readOnly
                className="bg-purple-500/10 border-purple-500/30 text-white"
                data-testid="referral-link-input"
              />
              <Button
                variant="outline"
                className="border-purple-500/50 text-purple-200 hover:bg-purple-500/20"
                onClick={() => copyToClipboard(referralData?.referralLink, 'Referral link')}
                data-testid="copy-link-button"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Reward Tiers */}
        <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Star className="h-5 w-5 text-amber-400" />
              Reward Tiers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {referralData?.rewards?.map((tier, index) => (
                <div 
                  key={tier.tier}
                  className={`flex items-center justify-between p-4 rounded-lg ${
                    tier.achieved 
                      ? 'bg-green-500/20 border border-green-500/30' 
                      : 'bg-purple-500/10'
                  }`}
                  data-testid={`tier-${tier.tier.toLowerCase()}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      tier.achieved ? 'bg-green-500/30' : 'bg-purple-500/20'
                    }`}>
                      {tier.achieved ? (
                        <CheckCircle2 className="h-6 w-6 text-green-400" />
                      ) : (
                        <span className="text-2xl">
                          {index === 0 ? '🥉' : index === 1 ? '🥈' : index === 2 ? '🥇' : '💎'}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="text-white font-semibold">{tier.tier}</p>
                      <p className="text-purple-300/60 text-sm">{tier.referrals} referrals needed</p>
                    </div>
                  </div>
                  <Badge className={tier.achieved 
                    ? 'bg-green-500/20 text-green-300 border-green-500/30'
                    : 'bg-purple-500/20 text-purple-300 border-purple-500/30'
                  }>
                    {tier.reward}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Referrals */}
        <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-400" />
              Recent Referrals
            </CardTitle>
          </CardHeader>
          <CardContent>
            {referralData?.referrals?.length === 0 ? (
              <div className="text-center py-8">
                <Gift className="h-12 w-12 text-purple-400 mx-auto mb-3 opacity-50" />
                <p className="text-purple-300/70">No referrals yet</p>
                <p className="text-purple-300/50 text-sm">Share your link to start earning!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {referralData?.referrals?.map((referral) => (
                  <div 
                    key={referral.id}
                    className="flex items-center justify-between p-3 bg-purple-500/10 rounded-lg"
                    data-testid={`referral-${referral.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                        {referral.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-white font-medium">{referral.name}</p>
                        <p className="text-purple-300/50 text-xs">{new Date(referral.date).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={referral.status === 'active' 
                        ? 'bg-green-500/20 text-green-300 border-green-500/30'
                        : 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                      }>
                        {referral.status}
                      </Badge>
                      <p className="text-amber-400 font-semibold mt-1">${referral.reward?.toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* How It Works */}
        <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white">How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="bg-purple-500/20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-purple-300">1</span>
                </div>
                <h4 className="text-white font-medium mb-1">Share Your Link</h4>
                <p className="text-purple-300/60 text-sm">Send your unique referral link to friends</p>
              </div>
              <div className="text-center">
                <div className="bg-purple-500/20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-purple-300">2</span>
                </div>
                <h4 className="text-white font-medium mb-1">They Sign Up</h4>
                <p className="text-purple-300/60 text-sm">Your friend creates an account using your link</p>
              </div>
              <div className="text-center">
                <div className="bg-purple-500/20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-purple-300">3</span>
                </div>
                <h4 className="text-white font-medium mb-1">You Both Earn</h4>
                <p className="text-purple-300/60 text-sm">Get $10 credit when they subscribe!</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default DailyDropReferrals;
